import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AppState } from '../../../client-redux/reducers';
import { Language } from '../../../languages/languageHandler';
import { ICurrency } from '../../../../libraries/currencies';
import { Button, Grid, GridCol, Group, Select, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useEffect, useMemo, useState } from 'react';
import {
  addHelpdeskContract,
  copyHelpdeskContract,
  deleteHelpdeskContract,
  EquipmentList,
  fetchHelpdeskContractEquipInfo,
  fetchHelpdeskContractInfo,
  IHelpdeskContractInfoResponse,
  IHelpdeskContractRequest,
  updateHelpdeskContract
} from '../../../../libraries/helpdesk/contracts';
import { useFormattedCurrencyInput } from '../../../utils/script';
import { fetchHelpdeskEntitiesList, IHelpdeskEntityResponse } from '../../../../libraries/helpdesk/entities';
import { LoginState } from '../../../client-redux/login/reducer';
import { billingPeriod, contractState, invoiceState, revType } from '../../utils';
import CustomModal from '../../../components/custom-modal';
import ChooseEntityModal from '../../components/choose-entity-modal';
import ContractCheckbox from '../../components/contracts-checkbox';
import ContractEquipmentTable from '../../components/contract-equipment-table';
import DeleteConfirmationModal from '../../../components/confirm-modal';
import Input from '../../../components/inputs';
import './styles.scss';
import { formatDateToISO } from '../../../utils/date';

export enum MODE {
  READ = 'read',
  EDIT = 'edit',
  NEW = 'new',
  COPY = 'copyBy',
  DELETE = 'delete'
}

const Contract = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const login = useSelector<AppState, LoginState>(state => state.login);
  const currencies = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const currency = currencies.find(c => c.currencyStr === 'EUR')!;
  const controller = new AbortController();
  const location = useLocation();
  const navigate = useNavigate();

  const { mode: modeParam, contractID } = useParams();

  const [loading, setLoading] = useState(false);
  const [, /* statusOperCode */ setStatusOperCode] = useState<number | undefined>();
  const [contract, setContract] = useState<IHelpdeskContractInfoResponse>();
  const [entities, setEntities] = useState<IHelpdeskEntityResponse>();

  const [type] = useState<string>('');
  const [entid] = useState<number>(0);
  const [name] = useState<string>('');
  const [nif] = useState<number>(0);
  const [offset] = useState<number>(0);
  const [limit] = useState<number>(0);
  const [, /* totalRowsResponse */ setTotalRowsResponse] = useState<number | undefined>();

  const [isEditable, setIsEditable] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [equipments, setEquipments] = useState<EquipmentList[]>([]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalMessage, setModalMessage] = useState<string>('');
  const [modalType, setModalType] = useState<'success' | 'error'>('success');
  const [operationType, setOperationType] = useState<'NEW' | 'EDIT' | 'DELETE'>('EDIT');

  const mode = useMemo(() => {
    switch (modeParam) {
      case MODE.NEW:
      case MODE.EDIT:
      case MODE.READ:
      case MODE.COPY:
        return modeParam as MODE;
      default:
        return MODE.READ;
    }
  }, [modeParam]);

  useEffect(() => {
    if (mode !== MODE.NEW) {
      getContractInfo();
      getContractEquipmentList();
      getEntityList();
    }

    if (mode === MODE.COPY) {
      form.setValues(prev => ({
        ...prev,
        agreeId: null,
        entid: null,
        entityName: ''
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, contractID]);

  useEffect(() => {
    const path = location.pathname;
    const editable = /\/helpdesk\/contract\/(edit|new|copyBy\/\d+)/.test(path);
    setIsEditable(editable);
  }, [location]);

  const getContractInfo = () => {
    if (!login.token) return;
    if (loading) {
      controller.abort();
    }
    setLoading(true);
    fetchHelpdeskContractInfo(Number(contractID), controller)
      .then(response => {
        if (response.statusOper.code === 0) {
          setContract(response);
          setStatusOperCode(response.statusOper.code);
        } else {
          setLoading(false);
          setStatusOperCode(response.statusOper.code);
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
        } else {
        }
      })
      .finally(() => setLoading(false));
  };

  const getContractEquipmentList = () => {
    if (!login.token) return;
    if (loading) {
      controller.abort();
    }
    setLoading(true);
    fetchHelpdeskContractEquipInfo(Number(contractID), controller)
      .then(response => {
        if (response.statusOper.code === 0) {
          setEquipments(response.list || []);
          setStatusOperCode(response.statusOper.code);
        } else {
          setLoading(false);
          setStatusOperCode(response.statusOper.code);
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
        } else {
        }
      })
      .finally(() => setLoading(false));
  };

  const getEntityList = () => {
    if (loading) {
      controller.abort();
    }
    setLoading(true);
    const shouldFetchTotalRows = offset === 0;
    fetchHelpdeskEntitiesList(controller, offset, limit, shouldFetchTotalRows, type, entid, name, nif)
      .then(response => {
        if (response.statusOper.code === 0) {
          setEntities(response);
          if (shouldFetchTotalRows && typeof response.totalRows === 'number') {
            setTotalRowsResponse(response.totalRows);
          }
          setStatusOperCode(response.statusOper.code);
          setLoading(false);
        } else {
          setLoading(false);
          setStatusOperCode(response.statusOper.code);
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
        } else {
        }
      })
      .finally(() => setLoading(false));
  };

  const deleteContract = () => {
    if (loading) {
      controller.abort();
    }
    setLoading(true);

    deleteHelpdeskContract(Number(contractID), controller)
      .then(response => {
        if (response.statusOper.code === 0) {
          setStatusOperCode(response.statusOper.code);
          setModalType('success');
          setModalMessage(lang.EDIT_AGREEMENT_CONTROLLER_ALERT_DELETE_SUCCESS);
          setShowModal(true);
          navigate('/helpdesk/contracts');
        } else {
          setStatusOperCode(response.statusOper.code);
          setModalType('error');
          setModalMessage(`${lang.EDIT_AGREEMENT_CONTROLLER_ALERT_DELETE_ERROR}: ${response.statusOper.message}`);
          setShowModal(true);

          setTimeout(() => {
            setShowModal(false);
            setShowDeleteModal(false);
          }, 2000);
        }
      })
      .catch((error: Error) => {
        if (error.name === 'AbortError') {
        } else {
          console.error('Error during contract deletion:', error);
          setModalType('error');
          setModalMessage('Error during contract deletion');
          setShowModal(true);

          setTimeout(() => {
            setShowModal(false);
            setShowDeleteModal(false);
          }, 2000);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const confirmDelete = () => {
    setShowDeleteModal(true);
  };

  const form = useForm<{ [key: string]: any }>({
    initialValues: {
      agreeId: 0,
      billingPeriod: 0,
      cancellationDate: '',
      date: '',
      entid: 0,
      entityName: '',
      equiType: 0,
      equipmentList: [] as EquipmentList[],
      firstLine: false,
      firstLineValue: 0,
      firstLineDate: '',
      handWork: false,
      invoiceState: 0,
      lastInvoiceDate: '',
      lastRevDate: '',
      newSWReleases: false,
      newSWReleasesValue: 0,
      newSWReleasesDate: '',
      nextInvoiceDate: '',
      parts: false,
      partsDate: '',
      partsValue: 0,
      processing: false,
      processingValue: 0,
      processingDate: '',
      renovDate: '',
      revType: 0,
      revValue: 0,
      secondLine: false,
      secondLineDate: '',
      secondLineValue: 0,
      slaId: 0,
      state: 0,
      validity: 0,
      value: '0',
      warrantyParts: false,
      warrantyPartsValue: 0,
      warrantyPartsDate: '',
      webPlatform: false,
      webPlatformDate: '',
      webPlatformValue: 0
    }
  });

  useEffect(() => {
    if (mode === MODE.COPY) {
      form.setValues({
        ...form.values,
        agreeId: 0,
        entid: 0,
        entityName: ''
      });
    } else if (mode === MODE.NEW) {
      form.setValues({
        ...form.values,
        entid: contract?.list[0]?.entid || 0
      });

      if (contract?.list[0]?.entid) {
        fetchHelpdeskEntitiesList(new AbortController(), 0, 1, false, '', Number(contract?.list[0]?.entid), '', 0).then(
          res => {
            const found = res.entities?.[0];
            if (found) {
              form.setFieldValue('entityName', found.name);
            }
          }
        );
      }
    } else if (contract?.list?.[0]) {
      const contractInfo = contract.list[0];
      const matchingEntity = entities?.entities.find(entity => entity.entid === contractInfo.entid);
      form.setValues({
        ...form.values,
        agreeId: contractInfo?.agreeId || 0,
        billingPeriod: contractInfo?.billingPeriod?.toString() || 0,
        cancellationDate: contractInfo?.cancellationDate ? new Date(contractInfo?.cancellationDate) : null,
        date: contractInfo?.date ? new Date(contractInfo?.date) : null,
        entid: contractInfo?.entid || 0,
        entityName: matchingEntity ? matchingEntity.name : '',
        equiType: contractInfo?.equiType || 0,
        equipmentList: contractInfo.equipmentList || [],
        firstLine: contractInfo?.firstLine || false,
        firstLineDate: contractInfo?.firstLineDate ? new Date(contractInfo?.firstLineDate) : null,
        firstLineValue: contractInfo?.firstLineValue || 0,
        handWork: contractInfo?.handWork || false,
        invoiceState: contractInfo?.invoiceState?.toString() || 0,
        lastInvoiceDate: contractInfo?.lastInvoiceDate ? new Date(contractInfo?.lastInvoiceDate) : null,
        lastRevDate: contractInfo?.lastRevDate ? new Date(contractInfo?.lastRevDate) : null,
        newSWReleases: contractInfo?.newSWReleases || false,
        newSWReleasesDate: contractInfo?.newSWReleasesDate ? new Date(contractInfo?.newSWReleasesDate) : null,
        newSWReleasesValue: contractInfo?.newSWReleasesValue || 0,
        nextInvoiceDate: contractInfo?.nextInvoiceDate ? new Date(contractInfo?.nextInvoiceDate) : null,
        parts: contractInfo?.parts || false,
        partsDate: contractInfo?.partsDate ? new Date(contractInfo?.partsDate) : null,
        partsValue: contractInfo?.partsValue || 0,
        processing: contractInfo?.processing || false,
        processingDate: contractInfo?.processingDate ? new Date(contractInfo?.processingDate) : null,
        processingValue: contractInfo?.processingValue || 0,
        renovDate: contractInfo?.renovDate ? new Date(contractInfo?.renovDate) : null,
        revType: contractInfo?.revType?.toString() || 0,
        revValue: contractInfo?.revValue || 0,
        secondLine: contractInfo?.secondLine || false,
        secondLineDate: contractInfo?.secondLineDate ? new Date(contractInfo?.secondLineDate) : null,
        secondLineValue: contractInfo?.secondLineValue || 0,
        slaId: contractInfo?.slaId || 0,
        state: contractInfo?.state?.toString() || 0,
        validity: contractInfo?.validity || 0,
        value: contractInfo?.value || 0,
        warrantyParts: contractInfo?.warrantyParts || false,
        warrantyPartsDate: contractInfo?.warrantyPartsDate ? new Date(contractInfo?.warrantyPartsDate) : null,
        warrantyPartsValue: contractInfo?.warrantyPartsValue || 0,
        webPlatform: contractInfo?.webPlatform || false,
        webPlatformDate: contractInfo?.webPlatformDate ? new Date(contractInfo?.webPlatformDate) : null,
        webPlatformValue: contractInfo?.webPlatformValue || 0
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract, entities, mode]);

  const messages = {
    NEW: {
      success: lang.EDIT_AGREEMENT_CONTROLLER_ALERT_ADD_SUCCESS || 'Contract added successfully',
      error: lang.EDIT_AGREEMENT_CONTROLLER_ALERT_ADD_ERROR || 'Error adding contract'
    },
    DELETE: {
      success: lang.EDIT_AGREEMENT_CONTROLLER_ALERT_DELETE_SUCCESS || 'Contract deleted successfully',
      error: lang.EDIT_AGREEMENT_CONTROLLER_ALERT_DELETE_ERROR || 'Error deleting contract',
      confirm: lang.EDIT_AGREEMENT_CONTROLLER_ALERT_DELETE_CONFIRM || 'Are you sure you want to cancel the contract'
    },
    EDIT: {
      success: lang.EDIT_AGREEMENT_CONTROLLER_ALERT_EDIT_SUCCESS || 'Contract edited successfully',
      error: lang.EDIT_AGREEMENT_CONTROLLER_ALERT_EDIT_ERROR || 'Error editing contract'
    }
  };

  const submitForm = async (formValues: any) => {
    const request: IHelpdeskContractRequest = {
      agreeId: formValues.agreeId,
      billingPeriod: Number(formValues.billingPeriod),
      cancellationDate: formatDateToISO(formValues.cancellationDate),
      date: formatDateToISO(formValues.date),
      entid: formValues.entid,
      equipmentList: formValues.equipmentList,
      equiType: formValues.equiType,
      firstLine: formValues.firstLine,
      firstLineDate: formatDateToISO(formValues.firstLineDate),
      firstLineValue: formValues.firstLineValue,
      handWork: formValues.handWork,
      invoiceState: Number(formValues.invoiceState),
      lastInvoiceDate: formatDateToISO(formValues.lastInvoiceDate),
      lastRevDate: formatDateToISO(formValues.lastRevDate),
      newSWReleases: formValues.newSWReleases,
      newSWReleasesDate: formatDateToISO(formValues.newSWReleasesDate),
      newSWReleasesValue: formValues.newSWReleasesValue,
      nextInvoiceDate: formatDateToISO(formValues.nextInvoiceDate),
      parts: formValues.parts,
      partsDate: formatDateToISO(formValues.partsDate),
      partsValue: formValues.partsValue,
      processing: formValues.processing,
      processingDate: formatDateToISO(formValues.processingDate),
      processingValue: formValues.processingValue,
      renovDate: formatDateToISO(formValues.renovDate),
      revType: Number(formValues.revType),
      revValue: formValues.revValue,
      secondLine: formValues.secondLine,
      secondLineDate: formatDateToISO(formValues.secondLineDate),
      secondLineValue: formValues.secondLineValue,
      slaId: formValues.slaId,
      state: Number(formValues.state),
      validity: formValues.validity,
      value: Number(formValues.value),
      warrantyParts: formValues.warrantyParts,
      warrantyPartsDate: formatDateToISO(formValues.warrantyPartsDate),
      warrantyPartsValue: formValues.warrantyPartsValue,
      webPlatform: formValues.webPlatform,
      webPlatformDate: formatDateToISO(formValues.webPlatformDate),
      webPlatformValue: formValues.webPlatformValue
    };

    const formController = new AbortController();

    try {
      let response;

      if (mode === MODE.COPY) {
        response = await copyHelpdeskContract(request as IHelpdeskContractRequest, formController);
      } else if (mode === MODE.NEW) {
        response = await addHelpdeskContract(request as IHelpdeskContractRequest, formController);
        setOperationType('NEW');
      } else if (mode === MODE.EDIT) {
        response = await updateHelpdeskContract(request as IHelpdeskContractRequest, formController);
        setOperationType('EDIT');
      } else if (mode === MODE.DELETE) {
        response = await deleteHelpdeskContract(Number(contractID), controller);
        setOperationType('DELETE');
      }

      if (response?.statusOper && response.statusOper.code !== 0) {
        setModalType('error');
        setModalMessage(`${messages[operationType].error}: ${response.statusOper.message}`);
        setShowModal(true);
      } else {
        setModalType('success');
        setModalMessage(messages[operationType].success);
        setShowModal(true);

        setTimeout(() => {
          setShowModal(false);
          if (operationType === 'NEW') {
            setTimeout(() => {
              navigate('/helpdesk/contracts');
            }, 1000);
          } else {
            getContractInfo();
          }
        }, 2000);
      }
    } catch (error) {
      console.error('Error during the API call:', error);
      setModalType('error');
      setModalMessage(`${messages[operationType].error}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setShowModal(true);
    }
  };

  const chooseEntid = (entid: number, name?: string) => {
    if (!entid) return;
    form.setFieldValue('entid', entid);
    form.setFieldValue('entityName', name || '');
    toggleChooseEntityModal();
  };

  const toggleChooseEntityModal = () => {
    setIsModalOpen(prev => !prev);
  };

  const hideBtn = () => {
    setIsModalOpen(false);
  };

  const handleRemoveEquipment = (eid: number) => {
    const updatedEquipments = form.values.equipmentList.filter((eq: { eid: number }) => eq.eid !== eid);
    form.setFieldValue('equipmentList', updatedEquipments);

    const removedEquipment = form.values.equipmentList.find((eq: { eid: number }) => eq.eid === eid);
    if (removedEquipment) {
      setEquipments(prev => [...prev.filter(eq => eq.eid !== eid), removedEquipment]);
    }
  };

  const contractCheckboxes = [
    {
      label: lang.HELPDESK_FIRST_LINE,
      selected: form.values.firstLine,
      value: form.values.firstLineValue,
      date: form.values.firstLineDate,
      field: 'firstLine'
    },
    {
      label: lang.HELPDESK_SECOND_LINE,
      selected: form.values.secondLine,
      value: form.values.secondLineValue,
      date: form.values.secondLineDate,
      field: 'secondLine'
    },
    {
      label: lang.HELPDESK_HARDWARE,
      selected: form.values.parts,
      value: form.values.partsValue,
      date: form.values.partsDate,
      field: 'parts'
    },
    {
      label: lang.HELPDESK_PROCESSING_SERVICE,
      selected: form.values.processing,
      value: form.values.processingValue,
      date: form.values.processingDate,
      field: 'processing'
    },
    {
      label: lang.HELPDESK_NEW_SOFTWARE_RELEASES,
      selected: form.values.newSWReleases,
      value: form.values.newSWReleasesValue,
      date: form.values.newSWReleasesDate,
      field: 'newSWReleases'
    },
    {
      label: lang.HELPDESK_REPLACEMENT_PARTS,
      selected: form.values.warrantyParts,
      value: form.values.warrantyPartsValue,
      date: form.values.warrantyPartsDate,
      field: 'warrantyParts'
    },
    {
      label: lang.HELPDESK_ACCESS_PLATFORM,
      selected: form.values.webPlatform,
      value: form.values.webPlatformValue,
      date: form.values.webPlatformDate,
      field: 'webPlatform'
    }
  ];

  const toggleFormCheckbox = (field: any) => {
    form.setFieldValue(field, !form.values[field as keyof typeof form.values]);
  };

  const {
    displayValue: valueDisplay,
    handleFocus: handleValueFocus,
    handleChange: handleValueChange,
    handleBlur: handleValueBlur
  } = useFormattedCurrencyInput(form.values.value, val => form.setFieldValue('value', val), currency);

  const {
    displayValue: revValueDisplay,
    handleFocus: handleRevFocus,
    handleChange: handleRevChange,
    handleBlur: handleRevBlur
  } = useFormattedCurrencyInput(form.values.revValue, val => form.setFieldValue('revValue', val), currency);

  const modeClassMap: Record<MODE, string> = {
    [MODE.NEW]: 'helpdesk-contract-new',
    [MODE.EDIT]: 'helpdesk-contract-edit',
    [MODE.COPY]: 'helpdesk-contract-copy',
    [MODE.READ]: 'helpdesk-contract-read',
    [MODE.DELETE]: 'helpdesk-contract-delete' // add this to satisfy TS
  };

  return (
    <div className={modeClassMap[mode]}>
      <ChooseEntityModal lang={lang} isOpen={isModalOpen} hideBtn={hideBtn} chooseEntid={chooseEntid} />
      <Grid>
        <GridCol span={{ base: 12, md: 12, lg: 8 }}>
          {/* Form initialization */}
          <form
            onSubmit={form.onSubmit(values => {
              submitForm(values);
            })}
          >
            {/* Agreement num */}
            <GridCol span={12}>
              <TextInput
                label={lang.HELPDESK_AGREEMENT_NUML}
                value={form.values.agreeId}
                disabled={!isEditable}
                placeholder={lang.HELPDESK_CONTRACT_NUMBER}
                {...form.getInputProps('agreeId')}
              />
            </GridCol>

            {/* Entid */}
            <GridCol span={12}>
              <Input
                label={lang.GLOBAL_CLIENT}
                value={`${form.values.entid} - ${form.values.entityName}`}
                onClick={toggleChooseEntityModal}
                disabled={!isEditable}
                isEditable={isEditable}
                search={toggleChooseEntityModal}
              />
            </GridCol>

            {/* Equipment type */}
            <GridCol span={12}>
              <TextInput
                label={lang.HELPDESK_EQUIPMENT_TYPE}
                value={form.values.equiType}
                disabled={!isEditable}
                {...form.getInputProps('equiType')}
              />
            </GridCol>

            {/* Checkbox  */}
            <GridCol span={12}>
              {contractCheckboxes.map(item => (
                <ContractCheckbox
                  key={item.field}
                  label={item.label}
                  selected={form.values[item.field]}
                  value={form.values[`${item.field}Value`]}
                  date={form.values[`${item.field}Date`]}
                  disabled={!isEditable}
                  onChange={() => toggleFormCheckbox(item.field)}
                  form={form}
                  field={item.field}
                />
              ))}
            </GridCol>

            {/* Date */}
            <GridCol span={12}>
              <DateInput
                label={lang.HELPDESK_DATE}
                value={form.values.date ? new Date(form.values.date) : null}
                disabled={!isEditable}
                placeholder={form.values.date ? '' : ''}
                {...form.getInputProps('date')}
                clearable
                valueFormat="DD/MM/YYYY"
              />
            </GridCol>

            {/* Value */}
            <GridCol span={12}>
              <TextInput
                label={lang.HELPDESK_VALUE}
                value={valueDisplay}
                onFocus={handleValueFocus}
                onChange={handleValueChange}
                onBlur={handleValueBlur}
                disabled={!isEditable}
              />
            </GridCol>

            {/* Contract years / Validity*/}
            <GridCol span={12}>
              <TextInput
                label={lang.HELPDESK_CONTRACT_YEARS}
                value={form.values.validity}
                disabled={!isEditable}
                {...form.getInputProps('validity')}
              />
            </GridCol>

            {/* Cancellation */}
            <GridCol span={12}>
              <DateInput
                label={lang.HELPDESK_CANCELLATION_DATE}
                value={form.values.cancellationDate ? new Date(form.values.cancellationDate) : null}
                disabled={!isEditable}
                placeholder={form.values.cancellationDate ? '' : ''}
                {...form.getInputProps('cancellationDate')}
                clearable
                valueFormat="DD/MM/YYYY"
              />
            </GridCol>

            {/* Last renew date */}
            <GridCol span={12}>
              <DateInput
                label={lang.HELPDESK_LAST_RENEW_DATE}
                value={form.values.lastRevDate ? new Date(form.values.lastRevDate) : null}
                disabled={!isEditable}
                placeholder={form.values.lastRevDate ? '' : ''}
                {...form.getInputProps('lastRevDate')}
                clearable
                valueFormat="DD/MM/YYYY"
              />
            </GridCol>

            {/* Renew value */}
            <GridCol span={12}>
              <TextInput
                label={lang.HELPDESK_REV_VALUE}
                value={revValueDisplay}
                onFocus={handleRevFocus}
                onChange={handleRevChange}
                onBlur={handleRevBlur}
                disabled={!isEditable}
              />
            </GridCol>

            {/* Next Renew date */}
            <GridCol span={12}>
              <DateInput
                label={lang.HELPDESK_NEXT_RENOV_DATE}
                value={form.values.renovDate ? new Date(form.values.renovDate) : null}
                disabled={!isEditable}
                placeholder={form.values.renovDate ? '' : ''}
                {...form.getInputProps('renovDate')}
                clearable
                valueFormat="DD/MM/YYYY"
              />
            </GridCol>

            {/* Renew type */}
            <GridCol span={12}>
              <Select
                label={lang.HELPDESK_REV_TYPE}
                data={revType(lang)}
                value={form.values.revType.toString()}
                disabled={!isEditable}
                {...form.getInputProps('revType')}
              />
            </GridCol>

            {/* Last invoice date */}
            <GridCol span={12}>
              <DateInput
                label={lang.HELPDESK_LAST_INVOICE_DATE}
                value={form.values.lastInvoiceDate ? new Date(form.values.lastInvoiceDate) : null}
                disabled={!isEditable}
                placeholder={form.values.lastInvoiceDate ? '' : ''}
                {...form.getInputProps('lastInvoiceDate')}
                clearable
                valueFormat="DD/MM/YYYY"
              />
            </GridCol>

            {/* Next invoice date */}
            <GridCol span={12}>
              <DateInput
                label={lang.HELPDESK_NEXT_INVOICE_DATE}
                value={form.values.nextInvoiceDate ? new Date(form.values.nextInvoiceDate) : null}
                disabled={!isEditable}
                placeholder={form.values.nextInvoiceDate ? '' : ''}
                {...form.getInputProps('nextInvoiceDate')}
                clearable
                valueFormat="DD/MM/YYYY"
              />
            </GridCol>

            {/* Invoice state */}
            <GridCol span={12}>
              <Select
                label={lang.HELPDESK_INVOICE_STATE}
                data={invoiceState(lang)}
                value={form.values.invoiceState.toString()}
                disabled={!isEditable}
                {...form.getInputProps('invoiceState')}
              />
            </GridCol>

            {/* Billing period */}
            <GridCol span={12}>
              <Select
                label={lang.HELPDESK_BILLING_PERIOD}
                data={billingPeriod(lang)}
                value={form.values.billingPeriod.toString()}
                disabled={!isEditable}
                {...form.getInputProps('billingPeriod')}
              />
            </GridCol>

            {/* Contract state*/}
            <GridCol span={12}>
              <Select
                label={lang.HELPDESK_CONTRACT_STATE}
                data={contractState(lang)}
                value={form.values.state.toString()}
                disabled={!isEditable}
                {...form.getInputProps('state')}
              />
            </GridCol>

            {/* SLA code */}
            <GridCol span={12}>
              <TextInput
                label={lang.HELPDESK_SLA_CODE}
                value={form.values.slaId}
                disabled={!isEditable}
                {...form.getInputProps('slaId')}
              />
            </GridCol>
          </form>
        </GridCol>

        {mode !== MODE.NEW && mode !== MODE.COPY && equipments && (
          <GridCol span={{ base: 12, md: 12, lg: 4 }}>
            <ContractEquipmentTable
              title={lang.GLOBAL_EQUIPMENTS}
              optionsEquipments={equipments || []}
              contractEquipments={form.values.equipmentList}
              loading={loading}
              editable={isEditable}
              onRemoveEquipment={handleRemoveEquipment}
              form={form}
            />
          </GridCol>
        )}
      </Grid>
      <Group className="buttons">
        {/* Submit button */}
        {mode !== MODE.READ && (
          <Button
            radius="md"
            type="submit"
            className="save"
            onClick={e => {
              e.preventDefault();
              submitForm(form.values);
            }}
          >
            {lang.GLOBAL_SAVE_BUTTON}
          </Button>
        )}

        {/* Delete Button */}
        {mode !== MODE.READ && (
          <Button className="delete" onClick={() => confirmDelete()}>
            {lang.GLOBAL_DELETE}
          </Button>
        )}
      </Group>

      <DeleteConfirmationModal
        action={deleteContract}
        message={lang.EDIT_AGREEMENT_CONTROLLER_ALERT_DELETE_CONFIRM}
        successMessage={lang.DELETE_SUCCESS_MESSAGE}
        errorMessage={lang.DELETE_ERROR_MESSAGE}
        showModal={showDeleteModal}
        closeModal={() => setShowDeleteModal(false)}
      />

      <CustomModal
        opened={showModal}
        onClose={() => setShowModal(false)}
        message={modalMessage}
        backgroundColor={modalType === 'success' ? '#3BDF70' : '#E3353F'}
        textColor={modalType === 'success' ? 'black' : 'white'}
      />
    </div>
  );
};

export default Contract;
